import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import EmailPreviewIframe, { EmailPreviewIframeRef } from './EmailPreviewIframe';
import InvitationEmailEditHintBubble from './InvitationEmailEditHintBubble';
import {
  InvitationEmailEditHintPosition,
  InvitationEmailTemplateData,
} from './generateInvitationEmailTypes';

const standardTextFieldSx = {
  '& .MuiInput-underline:before': {
    borderBottom: 'none',
  },
  '& .MuiInput-underline:hover:before': {
    borderBottom: '1px solid rgba(0,0,0,0.42)',
  },
  '& .MuiInput-underline:after': {
    borderBottom: '1px solid rgba(0,0,0,0.42)',
  },
};

interface InvitationEmailFieldRowProps {
  label: string;
  tone: keyof typeof Palette;
  children: React.ReactNode;
}

const InvitationEmailFieldRow: React.FC<InvitationEmailFieldRowProps> = ({
  label,
  tone,
  children,
}) => (
  <Box sx={{ p: 2, borderBottom: `1px solid ${Palette[tone].border}` }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography
        sx={{
          minWidth: '60px',
          fontSize: '0.875rem',
          color: Palette[tone].disabledText || '#666',
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  </Box>
);

interface InvitationEmailComposePanelProps {
  tone: keyof typeof Palette;
  templateData: InvitationEmailTemplateData;
  templateHtml: string;
  fromEmail: string;
  onFromEmailChange: (value: string) => void;
  subject: string;
  onSubjectChange: (value: string) => void;
  customHtml: string;
  emailPreviewRef: React.RefObject<EmailPreviewIframeRef>;
  iframeContainerRef: React.RefObject<HTMLDivElement>;
  showSpeechBubble: boolean;
  elementPosition: InvitationEmailEditHintPosition | null;
  sending: boolean;
  onContentChange: (html: string) => void;
  onEnterEditMode: () => void;
  onElementPosition: (position: { top: number; left: number }) => void;
  onHideSpeechBubble: () => void;
  onCancel: () => void;
  onSend: () => void;
}

const InvitationEmailComposePanel: React.FC<InvitationEmailComposePanelProps> = ({
  tone,
  templateData,
  templateHtml,
  fromEmail,
  onFromEmailChange,
  subject,
  onSubjectChange,
  customHtml,
  emailPreviewRef,
  iframeContainerRef,
  showSpeechBubble,
  elementPosition,
  sending,
  onContentChange,
  onEnterEditMode,
  onElementPosition,
  onHideSpeechBubble,
  onCancel,
  onSend,
}) => (
  <Box
    sx={{
      border: `1px solid ${Palette[tone].border}`,
      borderRadius: '8px',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    }}
  >
    <Box
      sx={{
        p: 2,
        borderBottom: `1px solid ${Palette[tone].border}`,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmailIcon sx={{ color: Palette[tone].text }} />
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          New Message
        </Typography>
      </Box>
    </Box>

    <InvitationEmailFieldRow label="From:" tone={tone}>
      <TextField
        value={fromEmail}
        onChange={(e) => onFromEmailChange(e.target.value)}
        placeholder="Enter sender email"
        variant="standard"
        fullWidth
        sx={standardTextFieldSx}
      />
    </InvitationEmailFieldRow>

    <InvitationEmailFieldRow label="To:" tone={tone}>
      <Typography sx={{ flex: 1, fontSize: '0.875rem' }}>{templateData.contact.email}</Typography>
    </InvitationEmailFieldRow>

    <InvitationEmailFieldRow label="Subject:" tone={tone}>
      <TextField
        value={subject}
        onChange={(e) => onSubjectChange(e.target.value)}
        variant="standard"
        fullWidth
        sx={standardTextFieldSx}
      />
    </InvitationEmailFieldRow>

    <Box sx={{ p: 2 }}>
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          color: Palette[tone].disabledText || '#666',
          fontSize: '0.75rem',
          fontWeight: 500,
        }}
      >
        Email Content (click the highlighted section to edit your custom message)
      </Typography>

      <Box sx={{ position: 'relative' }}>
        <Box
          ref={iframeContainerRef}
          sx={{
            border: `1px solid ${Palette[tone].border}`,
            borderRadius: '4px',
            overflow: 'auto',
            backgroundColor: '#f5f5f5',
            position: 'relative',
            maxHeight: '600px',
            minHeight: '400px',
            isolation: 'isolate',
          }}
        >
          <EmailPreviewIframe
            ref={emailPreviewRef}
            templateHtml={templateHtml}
            customHtml={customHtml || ''}
            onContentChange={onContentChange}
            onEnterEditMode={onEnterEditMode}
            onExitEditMode={() => {}}
            onElementPosition={onElementPosition}
            onHideSpeechBubble={onHideSpeechBubble}
          />
        </Box>

        {showSpeechBubble && elementPosition && (
          <InvitationEmailEditHintBubble position={elementPosition} />
        )}
      </Box>
    </Box>

    <Box
      sx={{
        p: 2,
        borderTop: `1px solid ${Palette[tone].border}`,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2,
      }}
    >
      <StyledButton variant="outlined" onClick={onCancel}>
        Cancel
      </StyledButton>
      <StyledButton
        variant="contained"
        onClick={onSend}
        disabled={sending}
        startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
      >
        {sending ? 'Sending...' : 'Send'}
      </StyledButton>
    </Box>
  </Box>
);

export default InvitationEmailComposePanel;
